import { esc } from '../escape.js';
import { formatMoney } from '../currency.js';
import { DAY_NAMES, DEFAULT_PARTY_SIZES, slotsForDay, holdFor, durationLabel, toMinutes, fromMinutes } from '../bookingRules.js';
import { zonedParts, zonedToDate, weekdayIndex, todayIn } from '../scheduleTime.js';

// Tables panel (2026-10-22, free on every plan). Same shell as Schedule
// (.ts-layout sidebar + drawer). Two modes, from Settings > Home screen:
//   numbers - a plain list of table names for the checkout picker
//   full    - live floor plan + open tabs, layout editor, reservations, waitlist
// Mockup: claude.ai/artifact/RHVFJ2SoqrkBqB91h32yJx. Positions are stored on a
// 1000 x 700 grid and drawn as percentages, so the floor scales to any screen.

const W = 1000, H = 700, SNAP = 10;
const WAIT_PARTY_SIZES = [1, 2, 3, 4, 5, 6, 8];
const SHAPES = {
  round:  { label: 'Round',  w: 90,  h: 90,  seats: 2 },
  square: { label: 'Square', w: 110, h: 110, seats: 4 },
  long:   { label: 'Long',   w: 220, h: 110, seats: 6 },
  booth:  { label: 'Booth',  w: 150, h: 100, seats: 4 },
  bar:    { label: 'Bar seat', w: 50, h: 50, seats: 1 },
};
const STATUS = {
  free:     'Free',
  reserved: 'Reserved',
  seated:   'Seated',
  ordered:  'Ordered',
  bill:     'Wants bill',
  dirty:    'Clean up',
};
const ICONS = {
  floor: '<rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="3.5"/><rect x="3" y="14" width="7" height="7" rx="3.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/>',
  reservations: '<path d="M8 2v4"/><path d="M16 2v4"/><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M3 10h18"/><path d="m9 16 2 2 4-4"/>',
  waitlist: '<circle cx="9" cy="8" r="3.5"/><path d="M2.5 20a6.5 6.5 0 0 1 13 0"/><path d="M17 4.5a3.5 3.5 0 0 1 0 7"/><path d="M21.5 20a6.5 6.5 0 0 0-4-6"/>',
  layout: '<path d="M12 20h9"/><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z"/>',
  list: '<path d="M8 6h13"/><path d="M8 12h13"/><path d="M8 18h13"/><path d="M3 6h.01"/><path d="M3 12h.01"/><path d="M3 18h.01"/>',
};
const svg = (d, s = 16) => `<svg xmlns="http://www.w3.org/2000/svg" width="${s}" height="${s}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${d}</svg>`;

const clamp = (n, lo, hi) => Math.max(lo, Math.min(hi, n));
const snap = (n) => Math.round(n / SNAP) * SNAP;
const mins = (ms) => Math.max(0, Math.floor(ms / 60000));
const ago = (iso, now) => {
  const m = mins(now - new Date(iso).getTime());
  return m < 60 ? `${m}m` : `${Math.floor(m / 60)}h ${m % 60}m`;
};
// Times show in the BUSINESS's timezone (set from render data), so a device
// in another zone still reads the cafe's clock.
let TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;
const clock = (d) => {
  const { h, mi: m } = zonedParts(new Date(d), TZ);
  return `${h % 12 || 12}${m ? `:${String(m).padStart(2, '0')}` : ''}${h < 12 ? 'a' : 'p'}`;
};
// An hour of the cafe's day (0-23) as a label, for the timeline axis. Not clock(): that
// converts an instant to the business zone, and a device-local Date made from the hour
// would shift by the device's offset (axis 12p-6a on a Manila laptop, 2026-10-25).
const hourLabel = (h) => `${h % 12 || 12}${h % 24 < 12 ? 'a' : 'p'}`;
// Sunday = 0 for a "YYYY-MM-DD" business day (bookingRules.hours order).
const sundayIndex = (dateStr) => (weekdayIndex(dateStr) + 1) % 7;
const seatsLabel = (n) => `${n} ${n === 1 ? 'seat' : 'seats'}`;

// A table's live status from its tab, cleaning flag and today's bookings.
export const tableStatus = (table, tab, reservations, now = Date.now()) => {
  if (tab) {
    if (tab.billRequestedAt) return 'bill';
    return tab.items.some((l) => l.sentAt) ? 'ordered' : 'seated';
  }
  if (table.needsCleaning) return 'dirty';
  const soon = (reservations ?? []).find((r) => r.tableId === table.id && r.status === 'booked'
    && new Date(r.startsAt).getTime() - now < 30 * 60000
    && now - new Date(r.startsAt).getTime() < 30 * 60000);
  return soon ? 'reserved' : 'free';
};

const unpaid = (tab) => (tab?.items ?? []).filter((l) => !l.paidSaleId);
const lineTotal = (l) => l.unitPrice * l.quantity;
const tabTotal = (tab, lids = null) => unpaid(tab).filter((l) => !lids || lids.has(l.lid)).reduce((s, l) => s + lineTotal(l), 0);

class TablesView {
  _panel   = document.querySelector('#tablesPanel');
  _sidebar = document.querySelector('#tablesSidebar');
  _content = document.querySelector('#tablesContent');
  _data    = null;
  _handlers = {};
  _section = 'floor';
  _area    = null;
  _selId   = null;     // selected table on the floor
  _split   = null;     // Set of line ids being split off, or null
  _pick    = null;     // 'move' | 'join' while choosing a target table
  _guests  = 2;        // walk-in party size
  _draft   = null;     // layout editor working copy
  _removed = [];
  _removeArmed = null; // table whose Remove was tapped once
  _layoutSel = null;   // index into _draft
  _addingArea = false; // the "+ Area" name field is showing
  _resForm = null;     // new booking form state
  _resSel  = null;     // selected booking id
  _waitForm = { name: '', partySize: 2, phone: '', note: '', quotedMin: null };

  open() { this._panel.classList.remove('hidden', 'cashflow-exiting'); }

  close() {
    this._closeDrawer();
    this._panel.classList.add('cashflow-exiting');
    setTimeout(() => {
      this._panel.classList.add('hidden');
      this._panel.classList.remove('cashflow-exiting');
    }, 220);
  }

  isOpen() { return !this._panel.classList.contains('hidden'); }

  renderLoading() {
    this._content.innerHTML = '<div class="sch-loading"><span class="skel" style="height:44px;border-radius:14px"></span><span class="skel" style="height:420px;border-radius:18px"></span></div>';
  }

  get section() { return this._section; }
  get reservationDay() { return this._data?.day ?? todayIn(TZ); }

  // data: { mode, tables, tabs, reservations, dayReservations, day, waitlist,
  //         waitlistOn, reservationsOn, canEditLayout, now }
  render(data) {
    this._data = data;
    if (data.tz) TZ = data.tz;
    const sections = this._sections();
    if (!sections.some((s) => s.id === this._section)) this._section = sections[0].id;
    this._renderSidebar(sections);
    const areas = this._areas();
    if (!areas.includes(this._area)) this._area = areas[0] ?? 'Inside';
    if (this._section === 'list') this._renderList();
    else if (this._section === 'layout') this._renderLayout();
    else if (this._section === 'reservations') this._renderReservations();
    else if (this._section === 'waitlist') this._renderWaitlist();
    else this._renderFloor();
  }

  // Lets the controller jump straight to a table (e.g. after paying its tab).
  // Keeps the floor on that table's area (seating a party in "Inside" must not
  // leave the floor showing "Patio").
  select(tableId) {
    this._selId = tableId;
    this._section = 'floor';
    const area = this._data?.tables.find((t) => t.id === tableId)?.area;
    if (area) this._area = area;
  }
  // Open on a section (home cards' Open / Add a party). Unknown ids fall back in render.
  showSection(id) { this._section = id; this._split = null; this._pick = null; }

  bind(handlers) {
    this._handlers = handlers;
    document.addEventListener('click', (e) => {
      if (e.target.closest("[data-action='tables']")) handlers.onOpen();
    });
    this._panel.querySelector('.tables-back')?.addEventListener('click', () => handlers.onClose());
    const btn = this._panel.querySelector('#tablesNavBtn');
    btn?.addEventListener('click', () => (btn.getAttribute('aria-expanded') === 'true' ? this._closeDrawer() : this._openDrawer()));
    this._panel.querySelector('#tablesNavBackdrop')?.addEventListener('click', () => this._closeDrawer());
    this._content.addEventListener('click', (e) => this._onClick(e));
    this._content.addEventListener('change', (e) => this._onChange(e));
    this._content.addEventListener('input', (e) => this._onInput(e));
    this._content.addEventListener('pointerdown', (e) => this._onPointerDown(e));
    this._content.addEventListener('keydown', (e) => this._onKeyDown(e));
  }

  // ── Drawer + sidebar ─────────────────────────────────────────────────────────
  _openDrawer() {
    this._sidebar.classList.add('panel-sidebar--open');
    this._panel.querySelector('#tablesNavBackdrop')?.classList.add('panel-nav-backdrop--visible');
    this._panel.querySelector('#tablesNavBtn')?.setAttribute('aria-expanded', 'true');
  }

  _closeDrawer() {
    this._sidebar.classList.remove('panel-sidebar--open');
    this._panel.querySelector('#tablesNavBackdrop')?.classList.remove('panel-nav-backdrop--visible');
    this._panel.querySelector('#tablesNavBtn')?.setAttribute('aria-expanded', 'false');
  }

  _sections() {
    const d = this._data;
    if (d.mode !== 'full') return [{ id: 'list', c: '#0FA267', label: 'Tables', desc: 'Names for the checkout picker' }];
    const waiting = d.waitlist.filter((w) => w.status === 'waiting').length;
    const today = d.reservations.filter((r) => r.status !== 'cancelled').length;
    return [
      { id: 'floor', c: '#0FA267', label: 'Floor', desc: 'Live table status' },
      ...(d.reservationsOn ? [{ id: 'reservations', c: '#7C3AED', label: 'Reservations', desc: today ? `${today} today` : 'None today', count: 0 }] : []),
      ...(d.waitlistOn ? [{ id: 'waitlist', c: '#EA580C', label: 'Waitlist', desc: waiting ? `${waiting} waiting` : 'Nobody waiting', count: waiting }] : []),
      ...(d.canEditLayout ? [{ id: 'layout', c: '#2563EB', label: 'Edit layout', desc: 'Tables, seats &amp; areas' }] : []),
    ];
  }

  _renderSidebar(sections) {
    this._sidebar.innerHTML = `<p class="ts-side-label">Sections</p>${sections.map((n) => `
      <button class="pb-nav-item ts-tab${this._section === n.id ? ' ts-tab--active' : ''}" data-section="${n.id}" type="button" style="--nav-c:${n.c}">
        <span class="pb-nav-icon">${svg(ICONS[n.id], 18)}</span>
        <span class="pb-nav-text"><span class="pb-nav-name">${n.label}</span><span class="pb-nav-desc">${n.desc}</span></span>
        ${n.count ? `<span class="sch-nav-count" aria-label="${n.count} waiting">${n.count}</span>` : ''}
      </button>`).join('')}`;
    this._sidebar.querySelectorAll('.ts-tab').forEach((b) => b.addEventListener('click', () => {
      this._goSection(b.dataset.section);
      this._closeDrawer();
    }));
  }

  _goSection(id) {
    if (this._section === 'layout' && id !== 'layout') this._draft = null;
    this._section = id;
    this._split = null;
    this._pick = null;
    this._handlers.onSection?.(id);
    this.render(this._data);
  }

  _areas() {
    const src = this._section === 'layout' && this._draft ? this._draft : this._data.tables;
    const list = [...new Set(src.map((t) => t.area || 'Inside'))];
    if (this._area && !list.includes(this._area) && this._section === 'layout') list.push(this._area);
    return list.length ? list : ['Inside'];
  }

  _areaTabs(extra = '') {
    const areas = this._areas();
    if (areas.length < 2 && !extra) return '';
    return `<div class="tb-areas" role="tablist" aria-label="Area">${areas.map((a) => `
      <button type="button" role="tab" aria-selected="${a === this._area}" class="tb-area${a === this._area ? ' tb-area--on' : ''}" data-area="${esc(a)}">${esc(a)}</button>`).join('')}${extra}</div>`;
  }

  // ── Floor ────────────────────────────────────────────────────────────────────
  _tabFor(tableId) { return this._data.tabs.find((t) => t.tableId === tableId) ?? null; }

  _renderFloor() {
    const d = this._data;
    if (!d.tables.length) {
      this._content.innerHTML = `
        <div class="tb-empty">
          <span class="tb-empty-icon" aria-hidden="true">${svg(ICONS.floor, 28)}</span>
          <h3>No tables yet</h3>
          <p>${d.canEditLayout ? 'Draw your floor once: drag tables in, name them, and you\'re set.' : 'Ask a manager to set up the floor plan.'}</p>
          ${d.canEditLayout ? '<button type="button" class="btn primary" data-go="layout">Set up tables</button>' : ''}
        </div>`;
      return;
    }
    const inArea = d.tables.filter((t) => (t.area || 'Inside') === this._area);
    const statuses = d.tables.map((t) => tableStatus(t, this._tabFor(t.id), d.reservations, d.now));
    const count = (s) => statuses.filter((x) => x === s).length;
    const seated = count('seated') + count('ordered') + count('bill');
    const next = d.reservations.filter((r) => r.status === 'booked' && new Date(r.startsAt).getTime() > d.now - 30 * 60000)[0];
    const sel = d.tables.find((t) => t.id === this._selId) ?? null;
    if (!sel) this._selId = null;

    this._content.innerHTML = `
      <div class="tb-floor">
        <div class="tb-bar">
          ${this._areaTabs()}
          <p class="tb-summary"><b>${seated}</b> seated · <b>${count('free')}</b> free${next ? ` · next booking <b>${clock(next.startsAt)} ${esc(next.name)}, ${next.partySize}</b>` : ''}</p>
        </div>
        ${this._pick ? `<div class="tb-pick-note">${this._pick === 'move' ? 'Tap the table to move this tab to.' : 'Tap the table to join with this one.'} <button type="button" class="tb-link" data-pick-cancel="1">Cancel</button></div>` : ''}
        <div class="tb-floor-main">
          <div class="tb-canvas-wrap">
            <div class="tb-canvas" role="group" aria-label="Floor plan, ${esc(this._area)}">
              ${inArea.map((t) => this._tableMarkup(t, tableStatus(t, this._tabFor(t.id), d.reservations, d.now))).join('')}
            </div>
            <p class="tb-swipe-hint">Swipe sideways to see the whole floor.</p>
          </div>
          <aside class="tb-detail" aria-live="polite">${sel ? this._detailMarkup(sel) : this._detailEmpty()}</aside>
        </div>
        <div class="tb-legend">${Object.entries(STATUS).map(([k, v]) => `<span><i class="tb-dot tb-dot--${k}"></i>${v === 'Clean up' ? 'Needs cleaning' : v}</span>`).join('')}</div>
      </div>`;
  }

  _tableMarkup(t, status) {
    const tab = this._tabFor(t.id);
    const now = this._data.now;
    let meta = `${seatsLabel(t.seats)}`;
    if (tab) meta = `${tab.guests} · ${ago(tab.openedAt, now)}`;
    else if (status === 'reserved') {
      const r = this._data.reservations.find((x) => x.tableId === t.id && x.status === 'booked');
      if (r) meta = `${clock(r.startsAt)} · ${r.partySize}`;
    } else if (status === 'dirty') meta = 'Mark clean';
    const pos = `left:${t.x / W * 100}%;top:${t.y / H * 100}%;width:${t.w / W * 100}%;height:${t.h / H * 100}%`;
    const on = t.id === this._selId;
    return `
      <button type="button" class="tb-table tb-table--${status} tb-shape--${t.shape}${on ? ' tb-table--sel' : ''}" style="${pos}" data-table="${t.id}" aria-pressed="${on}" aria-label="Table ${esc(t.name)}, ${STATUS[status]}">
        <b class="tb-table-name">${esc(t.name)}</b>
        <span class="tb-table-status">${STATUS[status]}</span>
        <span class="tb-table-meta">${esc(meta)}</span>
      </button>`;
  }

  _detailEmpty() {
    return `<div class="tb-detail-empty">${svg(ICONS.floor, 26)}<p>Tap a table to seat guests, add items or take payment.</p></div>`;
  }

  _detailMarkup(t) {
    const d = this._data;
    const tab = this._tabFor(t.id);
    const status = tableStatus(t, tab, d.reservations, d.now);
    const head = `
      <div class="tb-detail-head">
        <h3>Table ${esc(t.name)}</h3>
        <span class="tb-pill tb-pill--${status}">${STATUS[status]}</span>
        <span class="tb-detail-seats">${seatsLabel(t.seats)}</span>
      </div>`;
    if (tab) return head + this._tabMarkup(t, tab);
    if (status === 'dirty') {
      return `${head}<p class="tb-detail-text">The last party paid. Mark it clean when it's wiped down and it goes back to Free.</p>
        <button type="button" class="tb-btn tb-btn--pri" data-clean="${t.id}">Mark clean</button>`;
    }
    const res = d.reservations.find((r) => r.tableId === t.id && r.status === 'booked' && new Date(r.startsAt).getTime() > d.now - 30 * 60000);
    const resCard = res ? `
      <div class="tb-res-card">
        <b>${esc(res.name)} · party of ${res.partySize}</b>
        <span>Booked for ${clock(res.startsAt)}${res.note ? ` · ${esc(res.note)}` : ''}</span>
      </div>` : '';
    const waiting = d.waitlistOn ? d.waitlist.filter((w) => w.status === 'waiting') : [];
    return `${head}
      ${status === 'reserved' ? `${resCard}
        <button type="button" class="tb-btn tb-btn--pri" data-seat-res="${res.id}">They're here: seat ${esc(res.name)}</button>
        <p class="tb-detail-text">Or seat a walk-in for now:</p>` : `<p class="tb-detail-text">Ready for guests.${res ? ` Booked at ${clock(res.startsAt)} for ${esc(res.name)}.` : ''}</p>`}
      <div class="tb-guests">
        <span>Guests</span>
        <button type="button" class="tb-step" data-guests="-1" aria-label="Fewer guests">−</button>
        <b>${this._guests}</b>
        <button type="button" class="tb-step" data-guests="1" aria-label="More guests">+</button>
      </div>
      <button type="button" class="tb-btn ${status === 'reserved' ? '' : 'tb-btn--pri'}" data-seat="${t.id}">Seat walk-in</button>
      ${waiting.length ? `
        <div class="tb-sub-title">From the waitlist</div>
        <div class="tb-wait-picks">${waiting.slice(0, 4).map((w) => `
          <button type="button" class="tb-wait-pick" data-seat-wait="${w.id}">
            <b>${esc(w.name)}</b><span>${w.partySize} · waited ${ago(w.addedAt, d.now)}</span>
          </button>`).join('')}</div>` : ''}`;
  }

  _tabMarkup(t, tab) {
    const d = this._data;
    const lines = tab.items;
    const open = unpaid(tab);
    const unsent = open.filter((l) => !l.sentAt);
    const split = this._split;
    const total = tabTotal(tab);
    const splitTotal = split ? tabTotal(tab, split) : 0;
    const rows = lines.map((l) => {
      const tag = l.paidSaleId ? ['Paid', 'paid'] : l.sentAt ? ['Sent', 'sent'] : ['Not sent', 'new'];
      const variants = l.selectedVariants.map((v) => v.variantName).filter(Boolean).join(', ');
      const pickable = split && !l.paidSaleId;
      return `
        <li class="tb-line${l.paidSaleId ? ' tb-line--paid' : ''}">
          ${pickable ? `<input type="checkbox" class="tb-line-pick" data-split-line="${l.lid}"${split.has(l.lid) ? ' checked' : ''} aria-label="Pay ${esc(l.itemName)}">` : ''}
          <b class="tb-line-qty">${l.quantity}×</b>
          <span class="tb-line-name">${esc(l.itemName)}${variants ? `<small>${esc(variants)}</small>` : ''}${l.note ? `<small>${esc(l.note)}</small>` : ''}</span>
          <span class="tb-tag tb-tag--${tag[1]}">${tag[0]}</span>
          <span class="tb-line-price">${formatMoney(lineTotal(l))}</span>
          ${!l.sentAt && !l.paidSaleId && !split ? `<button type="button" class="tb-line-x" data-remove-line="${l.lid}" aria-label="Remove ${esc(l.itemName)}">×</button>` : ''}
        </li>`;
    }).join('');

    if (this._pick) {
      return `<p class="tb-detail-text">${this._pick === 'move' ? `Pick a free table to move Table ${esc(t.name)}'s tab to.` : `Pick another seated table. Its tab joins this one.`}</p>
        <button type="button" class="tb-btn" data-pick-cancel="1">Cancel</button>`;
    }

    return `
      <div class="tb-stats">
        <div><span>Guests</span><b class="tb-stat-guests"><button type="button" class="tb-step tb-step--sm" data-tab-guests="-1" aria-label="Fewer guests">−</button>${tab.guests}<button type="button" class="tb-step tb-step--sm" data-tab-guests="1" aria-label="More guests">+</button></b></div>
        <div><span>Seated</span><b>${ago(tab.openedAt, d.now)}</b></div>
        <div><span>Server</span><b>${esc(tab.serverName || '-')}</b></div>
      </div>
      <div class="tb-sub-title">${split ? 'Pick what this guest pays for' : 'Open tab'}</div>
      ${lines.length ? `<ul class="tb-lines">${rows}</ul>` : '<p class="tb-detail-text">Nothing ordered yet.</p>'}
      ${split ? `
        <div class="tb-total"><span>Selected</span><b>${formatMoney(splitTotal)}</b></div>
        <button type="button" class="tb-btn tb-btn--pri" data-pay-split="1"${split.size ? '' : ' disabled'}>Pay ${formatMoney(splitTotal)}</button>
        <button type="button" class="tb-btn" data-split-cancel="1">Cancel split</button>` : `
        <div class="tb-total"><span>${open.length < lines.length ? 'Still to pay' : 'Tab so far'}</span><b>${formatMoney(total)}</b></div>
        <div class="tb-row">
          <button type="button" class="tb-btn" data-add-items="${tab.id}">Add items</button>
          ${unsent.length ? `<button type="button" class="tb-btn tb-btn--send" data-send="${tab.id}">Send ${unsent.length} to kitchen</button>` : `<button type="button" class="tb-btn" data-bill="${tab.billRequestedAt ? 'off' : 'on'}">${tab.billRequestedAt ? 'Not ready to pay' : 'Wants the bill'}</button>`}
        </div>
        ${open.length ? `<button type="button" class="tb-btn tb-btn--pri" data-pay="${tab.id}">Pay ${formatMoney(total)}</button>` : ''}
        <div class="tb-links">
          <button type="button" class="tb-link" data-pick="move">Move table</button>
          <button type="button" class="tb-link" data-pick="join">Join tables</button>
          ${open.length > 1 ? '<button type="button" class="tb-link" data-split="1">Split bill</button>' : ''}
          <button type="button" class="tb-link tb-link--muted" data-close-tab="${tab.id}">Close tab</button>
        </div>`}`;
  }

  // ── Table list (Table numbers mode) ──────────────────────────────────────────
  _renderList() {
    if (!this._draft) {
      this._draft = this._data.tables.map((t) => ({ ...t }));
      this._removed = [];
    }
    const can = this._data.canEditLayout;
    this._content.innerHTML = `
      <div class="tb-list">
        <div class="tb-list-head">
          <div>
            <h3>Your tables</h3>
            <p>Checkout shows these so staff know where to bring each order. Want a floor plan and open tabs? Pick Full service in Settings &gt; Home screen.</p>
          </div>
        </div>
        <div class="tb-list-rows">
          ${this._draft.map((t, i) => `
            <div class="tb-list-row">
              <label class="tb-field"><span>Name</span><input type="text" maxlength="12" value="${esc(t.name)}" data-list-name="${i}"${can ? '' : ' disabled'} /></label>
              <label class="tb-field tb-field--sm"><span>Seats</span><input type="number" min="1" max="40" value="${t.seats}" data-list-seats="${i}"${can ? '' : ' disabled'} /></label>
              ${can ? `<button type="button" class="tb-icon-btn" data-list-remove="${i}" aria-label="Remove table ${esc(t.name)}">×</button>` : ''}
            </div>`).join('') || '<p class="tb-detail-text">No tables yet.</p>'}
        </div>
        ${can ? `
          <div class="tb-list-actions">
            <button type="button" class="tb-btn" data-list-add="1">+ Add table</button>
            <button type="button" class="tb-btn" data-list-add="10">+ Add 1 to 10</button>
            <span class="tb-spacer"></span>
            <button type="button" class="tb-btn tb-btn--pri" data-list-save="1">Save tables</button>
          </div>` : '<p class="tb-detail-text">Ask a manager to change the list.</p>'}
      </div>`;
  }

  // Next free spot on the grid for a new table (left to right, top to bottom).
  _freeSpot(w, h, area) {
    const taken = (this._draft ?? []).filter((t) => (t.area || 'Inside') === area);
    for (let y = 30; y + h <= H; y += 20) {
      for (let x = 30; x + w <= W; x += 20) {
        if (!taken.some((t) => x < t.x + t.w + 20 && x + w + 20 > t.x && y < t.y + t.h + 20 && y + h + 20 > t.y)) return { x, y };
      }
    }
    return { x: 30, y: 30 };
  }

  // A name no other table uses. With a source name ("T4", "Patio 2") the
  // copy counts on from its number ("T5"); otherwise the next plain number.
  _nextName(from = null) {
    const taken = new Set((this._draft ?? []).map((t) => String(t.name).trim().toLowerCase()));
    const m = from && /^(.*?)(\d+)$/.exec(String(from).trim());
    if (m) {
      for (let n = Number(m[2]) + 1; n < 1000; n++) {
        const name = `${m[1]}${n}`.slice(0, 12);
        if (!taken.has(name.toLowerCase())) return name;
      }
    } else if (from) {
      for (let n = 2; n < 1000; n++) {
        const name = `${String(from).trim().slice(0, 9)} ${n}`;
        if (!taken.has(name.toLowerCase())) return name;
      }
    }
    const nums = (this._draft ?? []).map((t) => parseInt(t.name, 10)).filter(Number.isFinite);
    let n = (nums.length ? Math.max(...nums) : 0) + 1;
    while (taken.has(String(n))) n++;
    return String(n);
  }

  _newTable(shape, area = this._area || 'Inside') {
    const s = SHAPES[shape] ?? SHAPES.square;
    const spot = this._freeSpot(s.w, s.h, area);
    return { id: null, name: this._nextName(), seats: s.seats, shape, area, x: spot.x, y: spot.y, w: s.w, h: s.h, canReserve: true };
  }

  // ── Layout editor ────────────────────────────────────────────────────────────
  _renderLayout() {
    if (!this._draft) {
      this._draft = this._data.tables.map((t) => ({ ...t }));
      this._removed = [];
      this._layoutSel = null;
    }
    const inArea = this._draft.map((t, i) => [t, i]).filter(([t]) => (t.area || 'Inside') === this._area);
    const sel = this._layoutSel != null ? this._draft[this._layoutSel] : null;
    const seats = this._draft.reduce((s, t) => s + Number(t.seats || 0), 0);
    this._content.innerHTML = `
      <div class="tb-layout">
        <div class="tb-bar">
          ${this._areaTabs(this._addingArea
            ? '<span class="tb-area-new"><input type="text" id="tbNewArea" maxlength="30" placeholder="Patio, Upstairs…" aria-label="New area name" /><button type="button" class="tb-area" data-area-ok="1">Add</button><button type="button" class="tb-area" data-area-cancel="1" aria-label="Cancel">×</button></span>'
            : '<button type="button" class="tb-area tb-area--add" data-area-add="1">+ Area</button>')}
          <p class="tb-summary">${this._draft.length} tables · ${seats} seats</p>
          <span class="tb-spacer"></span>
          <button type="button" class="tb-btn" data-layout-cancel="1">Cancel</button>
          <button type="button" class="tb-btn tb-btn--pri" data-layout-save="1">Save layout</button>
        </div>
        <div class="tb-layout-main">
          <div class="tb-palette">
            <p class="tb-sub-title">Add a table</p>
            <div class="tb-palette-grid">${Object.entries(SHAPES).map(([k, s]) => `
              <button type="button" class="tb-palette-item" data-add-shape="${k}">
                <i class="tb-palette-shape tb-palette-shape--${k}" aria-hidden="true"></i><span>${s.label}${k === 'bar' ? '' : ` ${s.seats}`}</span>
              </button>`).join('')}</div>
            <p class="tb-detail-text">Drag tables to move them. Drag the corner to resize. Arrow keys nudge the selected table. Open tabs are never affected.</p>
          </div>
          <div class="tb-canvas-wrap">
            <div class="tb-canvas tb-canvas--edit" id="tbEditCanvas" aria-label="Floor layout, ${esc(this._area)}">
              ${inArea.map(([t, i]) => `
                <div class="tb-table tb-table--edit tb-shape--${t.shape}${i === this._layoutSel ? ' tb-table--sel' : ''}" style="left:${t.x / W * 100}%;top:${t.y / H * 100}%;width:${t.w / W * 100}%;height:${t.h / H * 100}%" data-edit="${i}" tabindex="0" role="button" aria-label="Table ${esc(t.name)}, ${seatsLabel(t.seats)}. Arrow keys move it.">
                  <b class="tb-table-name">${esc(t.name)}</b><span class="tb-table-meta">${seatsLabel(t.seats)}</span>
                  ${i === this._layoutSel ? '<span class="tb-resize" data-resize="1" aria-hidden="true"></span>' : ''}
                </div>`).join('')}
              ${inArea.length ? '' : '<p class="tb-canvas-empty">Tap a table on the left to add it here.</p>'}
            </div>
            <p class="tb-swipe-hint">Swipe sideways to see the whole floor. Drag a table to move it.</p>
          </div>
          <aside class="tb-props">${sel ? this._propsMarkup(sel) : '<div class="tb-detail-empty"><p>Select a table to name it and set its seats.</p></div>'}</aside>
        </div>
      </div>`;
  }

  _propsMarkup(t) {
    const areas = this._areas();
    return `
      <h3 class="tb-props-title">Table ${esc(t.name)}</h3>
      <label class="tb-field"><span>Name or number</span><input type="text" maxlength="12" value="${esc(t.name)}" data-prop="name" /></label>
      <div class="tb-field"><span>Seats</span>
        <div class="tb-guests tb-guests--prop">
          <button type="button" class="tb-step" data-prop-seats="-1" aria-label="Fewer seats">−</button>
          <b>${t.seats}</b>
          <button type="button" class="tb-step" data-prop-seats="1" aria-label="More seats">+</button>
        </div>
      </div>
      <div class="tb-field"><span>Shape</span>
        <div class="tb-seg" role="radiogroup" aria-label="Shape">${['round', 'square', 'long', 'booth', 'bar'].map((k) => `
          <button type="button" class="tb-seg-btn${t.shape === k ? ' tb-seg-btn--on' : ''}" role="radio" aria-checked="${t.shape === k}" data-prop-shape="${k}">${SHAPES[k].label}</button>`).join('')}</div>
      </div>
      <label class="tb-field"><span>Area</span>
        <select data-prop="area">${areas.map((a) => `<option${a === t.area ? ' selected' : ''}>${esc(a)}</option>`).join('')}</select>
      </label>
      <label class="tb-check"><input type="checkbox" data-prop="canReserve"${t.canReserve !== false ? ' checked' : ''} /> Can take reservations</label>
      <span class="tb-spacer"></span>
      <button type="button" class="tb-btn" data-prop-dup="1">Duplicate</button>
      ${this._removeArmed === t
        ? `<button type="button" class="tb-btn tb-btn--danger tb-btn--armed" data-prop-remove="1">Tap again to remove ${esc(t.name)}</button>`
        : '<button type="button" class="tb-btn tb-btn--danger" data-prop-remove="1">Remove table</button>'}`;
  }

  // Moves / resizes a table on the edit canvas without re-rendering.
  _onPointerDown(e) {
    if (this._section !== 'layout') return;
    const el = e.target.closest('[data-edit]');
    if (!el || e.button > 0) return;
    const i = Number(el.dataset.edit);
    const resizing = !!e.target.closest('[data-resize]');
    if (this._layoutSel !== i) {
      this._layoutSel = i;
      this._renderLayout();
      const again = this._content.querySelector(`[data-edit="${i}"]`);
      if (!again) return;
      return this._startDrag(e, again, i, false);
    }
    this._startDrag(e, el, i, resizing);
  }

  _startDrag(e, el, i, resizing) {
    const canvas = this._content.querySelector('#tbEditCanvas');
    if (!canvas) return;
    e.preventDefault();
    const rect = canvas.getBoundingClientRect();
    const k = W / rect.width;
    const t = this._draft[i];
    const start = { px: e.clientX, py: e.clientY, x: t.x, y: t.y, w: t.w, h: t.h };
    let moved = false;
    const move = (ev) => {
      const dx = (ev.clientX - start.px) * k, dy = (ev.clientY - start.py) * k;
      if (Math.abs(dx) + Math.abs(dy) > 3) moved = true;
      if (resizing) {
        t.w = clamp(snap(start.w + dx), 40, W - t.x);
        t.h = clamp(snap(start.h + dy), 40, H - t.y);
      } else {
        t.x = clamp(snap(start.x + dx), 0, W - t.w);
        t.y = clamp(snap(start.y + dy), 0, H - t.h);
      }
      el.style.left = `${t.x / W * 100}%`;
      el.style.top = `${t.y / H * 100}%`;
      el.style.width = `${t.w / W * 100}%`;
      el.style.height = `${t.h / H * 100}%`;
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      if (moved) this._content.querySelector(`[data-edit="${i}"]`)?.focus();
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  }

  // A new area exists once a table is put in it (areas are just table labels).
  _addArea() {
    const name = (this._content.querySelector('#tbNewArea')?.value ?? '').trim().slice(0, 30);
    this._addingArea = false;
    if (name) { this._area = name; this._layoutSel = null; }
    this._rerender();
  }

  _onKeyDown(e) {
    if (e.target.id === 'tbNewArea') {
      if (e.key === 'Enter') { e.preventDefault(); this._addArea(); }
      if (e.key === 'Escape') { this._addingArea = false; this._rerender(); }
      return;
    }
    if (this._section !== 'layout') return;
    const el = e.target.closest('[data-edit]');
    if (!el) return;
    const i = Number(el.dataset.edit);
    const t = this._draft[i];
    const step = e.shiftKey ? 50 : SNAP;
    const moves = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] };
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this._layoutSel = i; this._renderLayout(); this._content.querySelector(`[data-edit="${i}"]`)?.focus(); return; }
    if (!moves[e.key]) return;
    e.preventDefault();
    t.x = clamp(t.x + moves[e.key][0], 0, W - t.w);
    t.y = clamp(t.y + moves[e.key][1], 0, H - t.h);
    this._layoutSel = i;
    this._renderLayout();
    this._content.querySelector(`[data-edit="${i}"]`)?.focus();
  }

  // ── Reservations ─────────────────────────────────────────────────────────────
  _renderReservations() {
    const d = this._data;
    const day = d.day;
    const isToday = day === todayIn(TZ);
    const list = d.dayReservations;
    const booked = list.filter((r) => r.status !== 'cancelled');
    const guests = booked.reduce((s, r) => s + r.partySize, 0);
    // Timeline covers the day's booking hours (plus one hold after the last
    // start) and any booking outside them; 11a-10p on a day with no bookings.
    const bh = d.bookingRules.hours[sundayIndex(day)];
    const baseFrom = bh.on ? Math.floor(toMinutes(bh.from) / 60) : 11;
    const baseTo = bh.on ? Math.min(24, Math.ceil((toMinutes(bh.to) + d.bookingRules.holdMin) / 60)) : 22;
    const hours = booked.map((r) => zonedParts(new Date(r.startsAt), TZ).h);
    const from = Math.min(baseFrom, ...hours);
    const to = Math.max(baseTo, from + 4, ...hours.map((h) => Math.min(24, h + 2)));
    const span = to - from;
    const start = zonedToDate(day, `${String(from).padStart(2, '0')}:00`, TZ);
    const pct = (iso) => clamp((new Date(iso).getTime() - start.getTime()) / (span * 3600000) * 100, 0, 100);
    const rows = d.tables.filter((t) => t.canReserve !== false);
    const sel = list.find((r) => r.id === this._resSel) ?? null;
    const nowPct = isToday ? pct(new Date().toISOString()) : null;
    const [ly, lm, ld] = day.split('-').map(Number);
    const label = new Date(ly, lm - 1, ld).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });

    this._content.innerHTML = `
      <div class="tb-res">
        <div class="tb-bar">
          <div class="tb-day">
            <button type="button" class="tb-step" data-day="-1" aria-label="Previous day">‹</button>
            <b>${isToday ? 'Today, ' : ''}${esc(label)}</b>
            <button type="button" class="tb-step" data-day="1" aria-label="Next day">›</button>
          </div>
          ${isToday ? '' : '<button type="button" class="tb-link" data-day="0">Today</button>'}
          <p class="tb-summary"><b>${booked.length}</b> ${booked.length === 1 ? 'booking' : 'bookings'} · <b>${guests}</b> ${guests === 1 ? 'guest' : 'guests'}</p>
        </div>
        <div class="tb-res-main">
          <div class="tb-timeline" role="table" aria-label="Bookings by table">
            <div class="tb-tl-head" role="row"><span role="columnheader">Table</span><div class="tb-tl-hours">${Array.from({ length: span }, (_, i) => `<span>${hourLabel(from + i)}</span>`).join('')}</div></div>
            ${rows.map((t) => `
              <div class="tb-tl-row" role="row">
                <span class="tb-tl-name" role="rowheader"><b>${esc(t.name)}</b><small>${seatsLabel(t.seats)}</small></span>
                <div class="tb-tl-track" style="--hours:${span}">
                  ${nowPct != null ? `<i class="tb-tl-now" style="left:${nowPct}%"></i>` : ''}
                  ${booked.filter((r) => r.tableId === t.id).map((r) => {
                    const end = new Date(new Date(r.startsAt).getTime() + r.durationMin * 60000).toISOString();
                    return `<button type="button" class="tb-tl-block tb-tl-block--${r.status}${r.id === this._resSel ? ' tb-tl-block--sel' : ''}" style="left:${pct(r.startsAt)}%;width:${Math.max(2, pct(end) - pct(r.startsAt))}%" data-res="${r.id}"><b>${esc(r.name)} · ${r.partySize}</b><span>${clock(r.startsAt)}</span></button>`;
                  }).join('')}
                </div>
              </div>`).join('')}
            ${booked.some((r) => !r.tableId) ? `
              <div class="tb-tl-row" role="row">
                <span class="tb-tl-name" role="rowheader"><b>No table</b></span>
                <div class="tb-tl-track tb-tl-track--list">${booked.filter((r) => !r.tableId).map((r) => `<button type="button" class="tb-tl-chip${r.id === this._resSel ? ' tb-tl-block--sel' : ''}" data-res="${r.id}">${clock(r.startsAt)} ${esc(r.name)} · ${r.partySize}</button>`).join('')}</div>
              </div>` : ''}
            ${rows.length ? '' : '<p class="tb-detail-text">Add tables in Edit layout to book them.</p>'}
          </div>
          <aside class="tb-detail">${sel ? this._resDetail(sel) : this._resFormMarkup()}</aside>
        </div>
      </div>`;
  }

  _resDetail(r) {
    const t = this._data.tables.find((x) => x.id === r.tableId);
    const end = new Date(new Date(r.startsAt).getTime() + r.durationMin * 60000);
    const statusText = { booked: 'Booked', seated: 'Seated', cancelled: 'Cancelled', no_show: 'No-show' }[r.status];
    return `
      <div class="tb-detail-head"><h3>${esc(r.name)}</h3><span class="tb-pill tb-pill--${r.status === 'booked' ? 'reserved' : r.status === 'seated' ? 'seated' : 'free'}">${statusText}</span></div>
      <div class="tb-res-card">
        <b>Party of ${r.partySize} · ${clock(r.startsAt)} to ${clock(end)}</b>
        <span>${t ? `Table ${esc(t.name)}` : 'No table yet'}${r.phone ? ` · ${esc(r.phone)}` : ''}</span>
        ${r.note ? `<span>${esc(r.note)}</span>` : ''}
      </div>
      ${r.status === 'booked' ? `
        ${t ? `<button type="button" class="tb-btn tb-btn--pri" data-seat-res="${r.id}">They're here: seat at ${esc(t.name)}</button>` : ''}
        <button type="button" class="tb-btn" data-res-status="no_show" data-res-id="${r.id}">Mark no-show</button>
        <button type="button" class="tb-btn tb-btn--danger" data-res-status="cancelled" data-res-id="${r.id}">Cancel booking</button>` : ''}
      <button type="button" class="tb-link" data-res-new="1">+ New reservation</button>`;
  }

  _resFormMarkup() {
    const d = this._data;
    const rules = d.bookingRules;
    const f = this._resForm ?? (this._resForm = { name: '', phone: '', partySize: 2, partyOther: false, time: null, timeOther: false, durationMin: null, tableId: null, note: '' });
    const hold = this._holdMin();
    const slots = this._slots();
    // A typed one-off time stays as typed; otherwise snap to the first open slot.
    if (!f.timeOther && (!f.time || !slots.some((s) => s.value === f.time && !s.full))) f.time = slots.find((s) => !s.full)?.value ?? null;
    const fit = this._fitTable(f.partySize, f.time, hold);
    // A picked table that no longer fits (booked then, or too small) drops
    // back to Pick for me - and says so, instead of switching quietly.
    let switched = null;
    const picked = f.tableId && d.tables.find((x) => x.id === f.tableId);
    if (picked && (!this._tableFree(f.tableId, f.time, hold) || picked.seats < f.partySize)) {
      switched = `${picked.name} ${picked.seats < f.partySize ? `only seats ${picked.seats}` : 'is booked then'}`;
      f.tableId = null;
    }
    const reservable = d.tables.filter((x) => x.canReserve !== false);
    const biggest = Math.max(0, ...reservable.map((x) => x.seats));
    const tableId = f.tableId ?? fit?.id ?? null;
    const t = d.tables.find((x) => x.id === tableId);
    const dayName = DAY_NAMES[sundayIndex(d.day)];
    const closed = !rules.hours[sundayIndex(d.day)].on;
    const partyOther = f.partyOther || !DEFAULT_PARTY_SIZES.includes(f.partySize);
    const usual = holdFor(rules, f.partySize);
    return `
      <h3 class="tb-props-title">New reservation</h3>
      <div class="tb-form-2">
        <label class="tb-field"><span>Name</span><input type="text" maxlength="60" value="${esc(f.name)}" data-res-f="name" autocomplete="off" /></label>
        <label class="tb-field"><span>Phone</span><input type="tel" maxlength="30" value="${esc(f.phone)}" data-res-f="phone" autocomplete="off" /></label>
      </div>
      <div class="tb-field"><span>Party size</span>
        <div class="tb-chips">
          ${DEFAULT_PARTY_SIZES.map((n) => `<button type="button" class="tb-chip${!partyOther && f.partySize === n ? ' tb-chip--on' : ''}" data-res-party="${n}" aria-pressed="${!partyOther && f.partySize === n}">${n}</button>`).join('')}
          <button type="button" class="tb-chip${partyOther ? ' tb-chip--on' : ''}" data-res-party-other="1" aria-pressed="${partyOther}">Other…</button>
        </div>
        ${partyOther ? `<input type="number" class="tb-other-input" min="1" max="99" value="${f.partySize}" data-res-party-n="1" aria-label="Party size" />` : ''}
      </div>
      <div class="tb-field"><span>Time</span>
        <div class="tb-chips tb-chips--time">
          ${slots.map((s) => `<button type="button" class="tb-chip${!f.timeOther && s.value === f.time ? ' tb-chip--on' : ''}${s.full ? ' tb-chip--full' : ''}" data-res-time="${s.value}" aria-pressed="${!f.timeOther && s.value === f.time}"${s.full ? ' disabled' : ''}>${s.label}</button>`).join('')}
          <button type="button" class="tb-chip${f.timeOther ? ' tb-chip--on' : ''}" data-res-time-other="1" aria-pressed="${f.timeOther}">Other time…</button>
        </div>
        ${f.timeOther ? `<input type="time" class="tb-other-input" value="${f.time ? fromMinutes(toMinutes(f.time)) : ''}" data-res-time-n="1" aria-label="Booking time" />` : ''}
        ${slots.some((s) => s.full) ? `<small class="tb-hint">Crossed-out times have no free table for ${f.partySize}.</small>` : ''}
        ${closed ? `<small class="tb-hint">No bookings on ${dayName}s (Settings &gt; Home screen). Use Other time for a one-off.</small>`
          : slots.length ? '' : '<small class="tb-hint">No times left on this day. Use Other time for a one-off.</small>'}
      </div>
      <div class="tb-field"><span>Table held for</span>
        <div class="tb-hold">
          <input type="number" class="tb-other-input" min="15" max="600" step="15" value="${hold}" data-res-hold="1" aria-label="Minutes the table is held" />
          <span>min</span>
          <small class="tb-hint">${f.durationMin != null && f.durationMin !== usual
            ? `Usual for ${f.partySize}: ${durationLabel(usual)}. <button type="button" class="tb-link" data-res-hold-reset="1">Use usual</button>`
            : `Usual for ${f.partySize}: ${durationLabel(usual)}`}</small>
        </div>
      </div>
      <label class="tb-field"><span>Table</span>
        <select data-res-f="tableId">
          <option value="">${fit ? `Pick for me (Table ${esc(fit.name)})` : 'No table yet'}</option>
          ${reservable.map((x) => {
            // Only tables that seat the party and are free then can be picked;
            // the rest stay listed (greyed) so staff can see why.
            const small = x.seats < f.partySize;
            const busy = !this._tableFree(x.id, f.time, hold);
            const why = small ? ' · too small' : busy ? ' · booked then' : '';
            return `<option value="${x.id}"${f.tableId === x.id ? ' selected' : ''}${small || busy ? ' disabled' : ''}>Table ${esc(x.name)} · ${seatsLabel(x.seats)}${why}</option>`;
          }).join('')}
        </select>
        ${!fit && reservable.length && biggest < f.partySize
          ? `<small class="tb-hint">No table seats ${f.partySize}. Save it without a table, then join tables when they arrive.</small>` : ''}
        ${switched ? `<small class="tb-hint tb-hint--note">Table ${esc(switched)}, so it's back on Pick for me.</small>` : ''}
      </label>
      <label class="tb-field"><span>Note (optional)</span><input type="text" maxlength="200" value="${esc(f.note)}" data-res-f="note" placeholder="Allergies, high chair, occasion" /></label>
      <span class="tb-spacer"></span>
      <button type="button" class="tb-btn tb-btn--pri" data-res-save="1"${f.time ? '' : ' disabled'}>Book${f.time ? ` ${clock(this._slotDate(f.time))}` : ''} for ${f.partySize}${t ? ` · Table ${esc(t.name)}` : ''}</button>`;
  }

  // "H:MM" on the open day, at the business's wall clock.
  _slotDate(value) {
    const [h, m] = value.split(':').map(Number);
    return zonedToDate(this._data.day, `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`, TZ);
  }

  // This booking's hold: what staff typed, else the usual for the party size.
  _holdMin() {
    const f = this._resForm;
    return f?.durationMin ?? holdFor(this._data.bookingRules, f?.partySize ?? 2);
  }

  // Start times from the owner's booking hours for this weekday (bookingRules.js).
  // Today, times more than 15 minutes ago drop off.
  _slots() {
    const d = this._data;
    const now = Date.now();
    const hold = this._holdMin();
    const party = this._resForm?.partySize ?? 2;
    const reservable = d.tables.some((t) => t.canReserve !== false);
    return slotsForDay(d.bookingRules, sundayIndex(d.day))
      .filter((value) => this._slotDate(value).getTime() >= now - 15 * 60000)
      .map((value) => ({ value, label: clock(this._slotDate(value)), full: reservable && !this._fitTable(party, value, hold) }));
  }

  _tableFree(tableId, time, durationMin = this._holdMin()) {
    if (!time) return true;
    const s = this._slotDate(time).getTime(), e = s + durationMin * 60000;
    return !this._data.dayReservations.some((r) => r.tableId === tableId && r.status === 'booked'
      && new Date(r.startsAt).getTime() < e && new Date(r.startsAt).getTime() + r.durationMin * 60000 > s);
  }

  // Smallest reservable table that fits the party and is free then.
  _fitTable(party, time, durationMin = this._holdMin()) {
    return this._data.tables
      .filter((t) => t.canReserve !== false && t.seats >= party && this._tableFree(t.id, time, durationMin))
      .sort((a, b) => a.seats - b.seats)[0] ?? null;
  }

  // ── Waitlist ─────────────────────────────────────────────────────────────────
  _renderWaitlist() {
    const d = this._data;
    const waiting = d.waitlist.filter((w) => w.status === 'waiting');
    const done = d.waitlist.filter((w) => w.status !== 'waiting').slice(-5).reverse();
    const free = d.tables.filter((t) => tableStatus(t, this._tabFor(t.id), d.reservations, d.now) === 'free');
    // A new party waits at least as long as what the parties ahead of it were told is left
    // (a party quoted 35m, 2m in, means about 35 more - not "About 5 min").
    const quotedLeft = Math.max(0, ...waiting.filter((w) => w.quotedMin != null)
      .map((w) => w.quotedMin - mins(d.now - new Date(w.addedAt).getTime())));
    const estimate = waiting.length
      ? Math.max(5, Math.ceil(Math.max(quotedLeft, (waiting.length + 1) * 10 / Math.max(1, free.length)) / 5) * 5)
      : 0;
    const f = this._waitForm;
    const quotes = d.waitlistQuotes;
    if (f.quotedMin == null) f.quotedMin = quotes[Math.min(2, quotes.length - 1)];
    const quoteOther = f.quoteOther || !quotes.includes(f.quotedMin);
    const wPartyOther = f.partyOther || !WAIT_PARTY_SIZES.includes(f.partySize);
    this._content.innerHTML = `
      <div class="tb-wait">
        <div class="tb-wait-main">
          <div class="tb-wait-hero">
            <div><span>Wait for a new party</span><b>${!free.length ? 'No table free right now' : waiting.length ? `About ${estimate} min` : 'No wait'}</b></div>
            <p>${free.length} free table${free.length === 1 ? '' : 's'} · ${waiting.length} waiting</p>
          </div>
          ${waiting.map((w) => {
            const waited = mins(d.now - new Date(w.addedAt).getTime());
            const over = w.quotedMin != null && waited > w.quotedMin;
            const fit = free.filter((t) => t.seats >= w.partySize).sort((a, b) => a.seats - b.seats)[0];
            const pctDone = w.quotedMin ? clamp(waited / w.quotedMin * 100, 4, 100) : 30;
            return `
              <div class="tb-wait-card${over ? ' tb-wait-card--over' : ''}">
                <div class="tb-wait-top">
                  <span class="tb-wait-size" aria-label="Party of ${w.partySize}">${w.partySize}</span>
                  <span class="tb-wait-who"><b>${esc(w.name)}</b><small>${esc(w.note || w.phone || '')}</small></span>
                  <span class="tb-wait-time"><b>${waited}m</b><small>${w.quotedMin != null ? `told ${w.quotedMin}m` : 'waiting'}</small></span>
                </div>
                <div class="tb-wait-bar"><i style="width:${pctDone}%"></i></div>
                <div class="tb-row">
                  ${fit ? `<button type="button" class="tb-btn tb-btn--pri" data-wait-seat="${w.id}" data-wait-table="${fit.id}">Seat at ${esc(fit.name)}</button>` : '<span class="tb-hint">No free table fits yet</span>'}
                  <select class="tb-wait-pick-table" data-wait-choose="${w.id}" aria-label="Seat ${esc(w.name)} at another table">
                    <option value="">Other table…</option>
                    ${free.map((t) => `<option value="${t.id}">Table ${esc(t.name)} · ${t.seats}</option>`).join('')}
                  </select>
                  <button type="button" class="tb-btn" data-wait-remove="${w.id}">Remove</button>
                </div>
              </div>`;
          }).join('') || '<p class="tb-detail-text tb-wait-none">Nobody is waiting.</p>'}
          ${done.length ? `<p class="tb-sub-title">Earlier</p><ul class="tb-wait-done">${done.map((w) => `<li><b>${esc(w.name)}</b> · ${w.partySize} · ${w.status === 'seated' ? 'seated' : 'removed'} ${clock(w.seatedAt ?? w.addedAt)}</li>`).join('')}</ul>` : ''}
        </div>
        <aside class="tb-detail">
          <h3 class="tb-props-title">Add a party</h3>
          <label class="tb-field"><span>Name</span><input type="text" maxlength="60" value="${esc(f.name)}" data-wait-f="name" autocomplete="off" /></label>
          <div class="tb-field"><span>Party size</span>
            <div class="tb-chips">
              ${WAIT_PARTY_SIZES.map((n) => `<button type="button" class="tb-chip${!wPartyOther && f.partySize === n ? ' tb-chip--on' : ''}" data-wait-party="${n}" aria-pressed="${!wPartyOther && f.partySize === n}">${n}</button>`).join('')}
              <button type="button" class="tb-chip${wPartyOther ? ' tb-chip--on' : ''}" data-wait-party-other="1" aria-pressed="${wPartyOther}">Other…</button>
            </div>
            ${wPartyOther ? `<input type="number" class="tb-other-input" min="1" max="99" value="${f.partySize}" data-wait-party-n="1" aria-label="Party size" />` : ''}
          </div>
          <label class="tb-field"><span>Phone (optional)</span><input type="tel" maxlength="30" value="${esc(f.phone)}" data-wait-f="phone" autocomplete="off" /></label>
          <label class="tb-field"><span>Note (optional)</span><input type="text" maxlength="200" value="${esc(f.note)}" data-wait-f="note" placeholder="Booth if possible" /></label>
          <div class="tb-field"><span>Wait you told them</span>
            <div class="tb-chips">
              ${quotes.map((n) => `<button type="button" class="tb-chip${!quoteOther && f.quotedMin === n ? ' tb-chip--on' : ''}" data-wait-quote="${n}" aria-pressed="${!quoteOther && f.quotedMin === n}">${n}m</button>`).join('')}
              <button type="button" class="tb-chip${quoteOther ? ' tb-chip--on' : ''}" data-wait-quote-other="1" aria-pressed="${quoteOther}">Other…</button>
            </div>
            ${quoteOther ? `<span class="tb-hold"><input type="number" class="tb-other-input" min="1" max="600" value="${f.quotedMin ?? ''}" data-wait-quote-n="1" aria-label="Minutes you told them" /><span>min</span></span>` : ''}
          </div>
          <span class="tb-spacer"></span>
          <button type="button" class="tb-btn tb-btn--pri" data-wait-add="1">Add to waitlist</button>
          <p class="tb-hint">Texting guests when their table is ready can come later. For now, call out their name.</p>
        </aside>
      </div>`;
  }

  // ── Events ───────────────────────────────────────────────────────────────────
  _rerender(focusSel) {
    this.render(this._data);
    if (focusSel) this._content.querySelector(focusSel)?.focus();
  }

  _onClick(e) {
    const h = this._handlers;
    const d = this._data;
    const q = (sel) => e.target.closest(sel);
    let el;

    if ((el = q('[data-go]'))) return this._goSection(el.dataset.go);
    if ((el = q('[data-area]'))) { this._area = el.dataset.area; this._layoutSel = null; return this._rerender(`[data-area="${CSS.escape(this._area)}"]`); }

    // Floor
    if ((el = q('[data-table]'))) {
      const id = el.dataset.table;
      if (this._pick) {
        const tab = this._tabFor(this._selId);
        const target = this._tabFor(id);
        if (!tab || id === this._selId) return;
        const mode = this._pick;
        this._pick = null;
        if (mode === 'move') { if (target) return h.onToast?.('That table already has guests. Use Join tables instead.'); return h.onMove(tab.id, id); }
        if (!target) return h.onToast?.('That table has no open tab to join.');
        return h.onMerge(target.id, tab.id);
      }
      this._selId = this._selId === id ? null : id;
      this._split = null;
      this._guests = Math.min(this._guests, 99);
      return this._rerender(`[data-table="${id}"]`);
    }
    if ((el = q('[data-guests]'))) { this._guests = clamp(this._guests + Number(el.dataset.guests), 1, 99); return this._rerender(`[data-guests="${el.dataset.guests}"]`); }
    if ((el = q('[data-seat]'))) return h.onSeat(el.dataset.seat, this._guests);
    if ((el = q('[data-seat-res]'))) return h.onSeatReservation(el.dataset.seatRes);
    if ((el = q('[data-seat-wait]'))) return h.onSeatWaitlist(el.dataset.seatWait, this._selId);
    if ((el = q('[data-clean]'))) return h.onClean(el.dataset.clean);
    if ((el = q('[data-add-items]'))) return h.onAddItems(el.dataset.addItems);
    if ((el = q('[data-send]'))) return h.onSend(el.dataset.send);
    if ((el = q('[data-remove-line]'))) return h.onRemoveLine(this._tabFor(this._selId)?.id, el.dataset.removeLine);
    if ((el = q('[data-tab-guests]'))) {
      const tab = this._tabFor(this._selId);
      if (tab) h.onGuests(tab.id, clamp(tab.guests + Number(el.dataset.tabGuests), 1, 99));
      return;
    }
    if ((el = q('[data-bill]'))) { const tab = this._tabFor(this._selId); if (tab) h.onBill(tab.id, el.dataset.bill === 'on'); return; }
    if ((el = q('[data-pay]'))) return h.onPay(el.dataset.pay, null);
    if (q('[data-split]')) { this._split = new Set(); return this._rerender(); }
    if (q('[data-split-cancel]')) { this._split = null; return this._rerender(); }
    if (q('[data-pay-split]')) { const tab = this._tabFor(this._selId); if (tab && this._split?.size) h.onPay(tab.id, [...this._split]); return; }
    if ((el = q('[data-pick]'))) { this._pick = el.dataset.pick; return this._rerender(); }
    if (q('[data-pick-cancel]')) { this._pick = null; return this._rerender(); }
    if ((el = q('[data-close-tab]'))) return h.onCloseTab(el.dataset.closeTab);

    // Table list (numbers mode)
    if ((el = q('[data-list-add]'))) {
      const n = Number(el.dataset.listAdd);
      if (n === 10) {
        const have = new Set(this._draft.map((t) => t.name));
        for (let i = 1; i <= 10; i++) if (!have.has(String(i))) this._draft.push({ ...this._newTable('square', 'Inside'), name: String(i) });
      } else this._draft.push(this._newTable('square', 'Inside'));
      this._rerender();
      this._content.querySelector(`[data-list-name="${this._draft.length - 1}"]`)?.focus();
      return;
    }
    if ((el = q('[data-list-remove]'))) {
      const [t] = this._draft.splice(Number(el.dataset.listRemove), 1);
      if (t?.id) this._removed.push(t.id);
      return this._rerender();
    }
    if (q('[data-list-save]')) return this._saveDraft();

    // Layout editor
    if ((el = q('[data-add-shape]'))) {
      this._draft.push(this._newTable(el.dataset.addShape));
      this._layoutSel = this._draft.length - 1;
      return this._rerender(`[data-edit="${this._layoutSel}"]`);
    }
    if (q('[data-area-add]')) { this._addingArea = true; return this._rerender('#tbNewArea'); }
    if (q('[data-area-cancel]')) { this._addingArea = false; return this._rerender(); }
    if (q('[data-area-ok]')) return this._addArea();
    if ((el = q('[data-prop-seats]'))) { const t = this._draft[this._layoutSel]; t.seats = clamp(t.seats + Number(el.dataset.propSeats), 1, 40); return this._rerender(`[data-prop-seats="${el.dataset.propSeats}"]`); }
    if ((el = q('[data-prop-shape]'))) {
      const t = this._draft[this._layoutSel];
      const s = SHAPES[el.dataset.propShape];
      t.shape = el.dataset.propShape;
      t.w = s.w; t.h = s.h;
      t.x = clamp(t.x, 0, W - t.w); t.y = clamp(t.y, 0, H - t.h);
      return this._rerender(`[data-prop-shape="${t.shape}"]`);
    }
    if (q('[data-prop-dup]')) {
      const t = this._draft[this._layoutSel];
      const copy = { ...t, id: null, name: this._nextName(t.name), ...this._freeSpot(t.w, t.h, t.area) };
      this._draft.push(copy);
      this._layoutSel = this._draft.length - 1;
      return this._rerender();
    }
    if (q('[data-prop-remove]')) {
      const t = this._draft[this._layoutSel];
      if (t.id && d.tabs.some((x) => x.tableId === t.id)) return h.onToast?.('This table has an open tab. Pay or move it first.');
      // Two taps: the first arms the button ("Tap again to remove").
      if (this._removeArmed !== t) { this._removeArmed = t; return this._rerender('[data-prop-remove]'); }
      this._removeArmed = null;
      this._draft.splice(this._layoutSel, 1);
      if (t.id) this._removed.push(t.id);
      this._layoutSel = null;
      return this._rerender();
    }
    if (q('[data-layout-cancel]')) { this._draft = null; this._layoutSel = null; return this._goSection('floor'); }
    if (q('[data-layout-save]')) return this._saveDraft();

    // Reservations
    if ((el = q('[data-day]'))) {
      // Changing the day keeps what's typed; only the time has to be picked again.
      this._resSel = null;
      if (this._resForm && !this._resForm.timeOther) this._resForm.time = null;
      return h.onDay(Number(el.dataset.day));
    }
    if ((el = q('[data-res]'))) { this._resSel = this._resSel === el.dataset.res ? null : el.dataset.res; return this._rerender(); }
    if (q('[data-res-new]')) { this._resSel = null; return this._rerender('[data-res-f="name"]'); }
    if ((el = q('[data-res-party]'))) { Object.assign(this._resForm, { partySize: Number(el.dataset.resParty), partyOther: false }); return this._rerender(`[data-res-party="${el.dataset.resParty}"]`); }
    if (q('[data-res-party-other]')) { this._resForm.partyOther = true; return this._rerender('[data-res-party-n]'); }
    if ((el = q('[data-res-time]'))) { Object.assign(this._resForm, { time: el.dataset.resTime, timeOther: false }); return this._rerender(`[data-res-time="${el.dataset.resTime}"]`); }
    if (q('[data-res-time-other]')) { this._resForm.timeOther = true; return this._rerender('[data-res-time-n]'); }
    if (q('[data-res-hold-reset]')) { this._resForm.durationMin = null; return this._rerender('[data-res-hold]'); }
    if ((el = q('[data-res-status]'))) return h.onReservationStatus(el.dataset.resId, el.dataset.resStatus);
    if (q('[data-res-save]')) {
      const f = this._resForm;
      if (!f.name.trim()) return h.onToast?.('Add a name for the booking.');
      if (!f.time) return h.onToast?.('Pick a time for the booking.');
      const hold = this._holdMin();
      const tableId = f.tableId || this._fitTable(f.partySize, f.time, hold)?.id || null;
      return h.onSaveReservation({
        name: f.name, phone: f.phone, partySize: f.partySize, note: f.note,
        startsAt: this._slotDate(f.time).toISOString(), durationMin: hold, tableId,
      });
    }

    // Waitlist
    if ((el = q('[data-wait-party]'))) { Object.assign(this._waitForm, { partySize: Number(el.dataset.waitParty), partyOther: false }); return this._rerender(`[data-wait-party="${el.dataset.waitParty}"]`); }
    if (q('[data-wait-party-other]')) { this._waitForm.partyOther = true; return this._rerender('[data-wait-party-n]'); }
    if ((el = q('[data-wait-quote]'))) { Object.assign(this._waitForm, { quotedMin: Number(el.dataset.waitQuote), quoteOther: false }); return this._rerender(`[data-wait-quote="${el.dataset.waitQuote}"]`); }
    if (q('[data-wait-quote-other]')) { this._waitForm.quoteOther = true; return this._rerender('[data-wait-quote-n]'); }
    if (q('[data-wait-add]')) {
      const f = this._waitForm;
      if (!f.name.trim()) return h.onToast?.('Add a name so you can call them.');
      return h.onAddWaitlist({ ...f });
    }
    if ((el = q('[data-wait-seat]'))) return h.onSeatWaitlist(el.dataset.waitSeat, el.dataset.waitTable);
    if ((el = q('[data-wait-remove]'))) return h.onWaitlistStatus(el.dataset.waitRemove, 'removed');
  }

  _onChange(e) {
    const t = e.target;
    if (t.matches('[data-split-line]')) {
      if (t.checked) this._split.add(t.dataset.splitLine); else this._split.delete(t.dataset.splitLine);
      return this._rerender(`[data-split-line="${t.dataset.splitLine}"]`);
    }
    if (t.matches('[data-prop="area"]')) { this._draft[this._layoutSel].area = t.value; this._area = t.value; return this._rerender(); }
    if (t.matches('[data-prop="canReserve"]')) { this._draft[this._layoutSel].canReserve = t.checked; return; }
    if (t.matches('[data-list-seats]')) { this._draft[Number(t.dataset.listSeats)].seats = clamp(parseInt(t.value, 10) || 1, 1, 40); return; }
    if (t.matches('[data-res-f="tableId"]')) { this._resForm.tableId = t.value || null; return this._rerender('[data-res-f="tableId"]'); }
    // "Other…" one-offs: applied on change (not every keystroke) so the time
    // list and free tables update once the number is in.
    if (t.matches('[data-res-party-n]')) { this._resForm.partySize = clamp(parseInt(t.value, 10) || 1, 1, 99); return this._rerender('[data-res-party-n]'); }
    if (t.matches('[data-res-time-n]')) {
      if (!t.value) return;
      const [hh, mm] = t.value.split(':').map(Number);
      this._resForm.time = `${hh}:${mm}`;
      return this._rerender('[data-res-time-n]');
    }
    if (t.matches('[data-res-hold]')) { this._resForm.durationMin = clamp(Math.round((parseInt(t.value, 10) || 90) / 5) * 5, 15, 600); return this._rerender('[data-res-hold]'); }
    if (t.matches('[data-wait-party-n]')) { this._waitForm.partySize = clamp(parseInt(t.value, 10) || 1, 1, 99); return this._rerender('[data-wait-party-n]'); }
    if (t.matches('[data-wait-quote-n]')) { this._waitForm.quotedMin = clamp(parseInt(t.value, 10) || 0, 0, 600); return this._rerender('[data-wait-quote-n]'); }
    if (t.matches('[data-wait-choose]') && t.value) return this._handlers.onSeatWaitlist(t.dataset.waitChoose, t.value);
  }

  _onInput(e) {
    const t = e.target;
    if (t.matches('[data-prop="name"]')) {
      this._draft[this._layoutSel].name = t.value;
      const el = this._content.querySelector(`[data-edit="${this._layoutSel}"] .tb-table-name`);
      if (el) el.textContent = t.value;
      return;
    }
    if (t.matches('[data-list-name]')) { this._draft[Number(t.dataset.listName)].name = t.value; return; }
    if (t.matches('[data-res-f]') && t.dataset.resF !== 'tableId') { this._resForm[t.dataset.resF] = t.value; return; }
    // "Other…" numbers count as soon as they're typed (Add / Book work without
    // Enter); the time list and tables refresh on change, which keeps focus.
    const n = parseInt(t.value, 10);
    if (t.matches('[data-wait-party-n]') && n >= 1) { this._waitForm.partySize = clamp(n, 1, 99); return; }
    if (t.matches('[data-wait-quote-n]') && n >= 0) { this._waitForm.quotedMin = clamp(n, 0, 600); return; }
    if (t.matches('[data-res-party-n]') && n >= 1) { this._resForm.partySize = clamp(n, 1, 99); return; }
    if (t.matches('[data-res-hold]') && n >= 15) { this._resForm.durationMin = clamp(n, 15, 600); return; }
    if (t.matches('[data-wait-f]')) { this._waitForm[t.dataset.waitF] = t.value; }
  }

  _saveDraft() {
    const names = this._draft.map((t) => String(t.name).trim());
    if (names.some((n) => !n)) return this._handlers.onToast?.('Every table needs a name or number.');
    if (new Set(names.map((n) => n.toLowerCase())).size !== names.length) return this._handlers.onToast?.('Two tables have the same name. Give each one its own.');
    this._handlers.onSaveLayout(this._draft.map((t) => ({ ...t, name: String(t.name).trim() })), this._removed);
  }

  // Called by the controller after a successful save / add.
  afterLayoutSaved() { this._draft = null; this._removed = []; this._layoutSel = null; if (this._section === 'layout') this._section = 'floor'; }
  afterReservationSaved() { this._resForm = null; this._resSel = null; }
  afterWaitlistAdded() { this._waitForm = { name: '', partySize: 2, phone: '', note: '', quotedMin: null }; }
  clearSplit() { this._split = null; this._pick = null; }
}

export default new TablesView();
